/**
 * LeadLoop domain vocabulary — the closed sets that define the pipeline.
 *
 * Snake_case values are what the operational database stores; the display
 * form shown in the UI is derived with lccStatusLabel()/lccStatusValue().
 * External systems (Airtable, web views, Workers) adapt to this vocabulary,
 * never the other way around.
 */

export const LCC_LEAD_STATUSES = [
  'new',
  'needs_review',
  'ready_for_verification',
  'verified',
  'qualified',
  'callback_needed',
  'waiting_on_client',
  'appointment_set',
  'estimate_sent',
  'converted',
  'lost',
  'recycle_later',
  'spam',
] as const;

export type LccLeadStatus = (typeof LCC_LEAD_STATUSES)[number];

export const LCC_LEAD_SOURCES = [
  'website_form',
  'phone_call',
  'google_ads',
  'facebook_ads',
  'referral',
  'email',
  'chat',
  'manual_entry',
  'imported',
] as const;

export type LccLeadSource = (typeof LCC_LEAD_SOURCES)[number];

/** snake_case → "Title Case" (deterministic, reversible via lccStatusValue). */
export function lccStatusLabel(value: string): string {
  return value
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** "Title Case" → snake_case. Accepts snake_case input unchanged. */
export function lccStatusValue(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, '_');
}

/** Display labels for every LCC lead status, in pipeline order. */
export const LCC_LEAD_STATUS_LABELS = LCC_LEAD_STATUSES.map(lccStatusLabel);

export function isLccLeadStatus(value: string): value is LccLeadStatus {
  return (LCC_LEAD_STATUSES as readonly string[]).includes(value);
}

/** Lead statuses that count as "no longer an open opportunity". */
export const LCC_CLOSED_STATUSES = ['converted', 'lost', 'spam', 'recycle_later'] as const;

export function isOpenLeadStatus(status: string): boolean {
  return !(LCC_CLOSED_STATUSES as readonly string[]).includes(status);
}

export const LCC_LEAD_PRIORITIES = ['low', 'normal', 'high'] as const;

export const LCC_INTERACTION_TYPES = [
  'call',
  'text',
  'email',
  'in_person',
  'new_info',
  'internal_note',
] as const;

export const LCC_VERIFICATION_METHODS = [
  'call',
  'email',
  'text',
  'in_person',
  'ai_receptionist',
  'other',
] as const;
